// The call screen (BRIEF 6.3): remote video full-bleed, a small draggable
// self-view, a floating controls tray, and a knock banner for the host.

import { useEffect, useMemo, useRef, useState } from "react";
import type { JSX } from "react";
import { ROOT_HANDLE, roomLink, roomOrigin, surfaceKeepsPrivate } from "@breeze/core";
import { useCallStore } from "../hooks/useCallStore";
import type { RoomKind } from "../lib/wsClient";
import { BlurLoading } from "../components/BlurLoading";
import { DraggableSelfView } from "../components/DraggableSelfView";
import { BreezePanel, type RecordingShown } from "../components/BreezePanel";
import { RecordingMark } from "../components/RecordingMark";
import { RecordingOffBar } from "../components/RecordingOffBar";
import { playDing, playWhoosh } from "../lib/whoosh";
import { BreezeStrip } from "../components/BreezeStrip";
import { Popout } from "../components/Popout";
import { PopoutOffer } from "../components/PopoutOffer";
import { CallMenu, ControlsTray } from "../components/ControlsTray";
import { WaitingTrivia, showsTrivia } from "../components/WaitingTrivia";
import { WaitingLine, waitingText } from "../components/WaitingLine";
import { useDesktopControls } from "../hooks/useDesktopControls";
import { usePopout } from "../hooks/usePopout";
import { shareSurface } from "../lib/pip";
import { CadencePanel } from "../components/CadencePanel";
import { CallPrepWorkspace } from "../components/CallPrepWorkspace";
import { WorkspaceSheet } from "../components/WorkspaceSheet";
import { ChatPanel } from "../components/ChatPanel";
import { usePrepNow } from "../hooks/usePrep";
import { KnockBanner } from "../components/KnockBanner";
import { LateGuestBanner } from "../components/LateGuestBanner";
import { CallClock } from "../components/CallClock";
import { PhoneDialer } from "../components/PhoneDialer";
import { BlurPreviewSheet } from "../components/BlurPreviewSheet";
import { api, ApiError } from "../lib/api";
import { getKeyHandle, getUserKey } from "../lib/storage";
import { BREEZE_PANEL_ENABLED } from "../lib/playbooks";
import { RemoteTile, RevealContext } from "../components/RemoteTile";
import { HandChip } from "../components/HandChip";
import { VideoView } from "../components/VideoView";
import { TrashIcon } from "../components/icons";

/** Pop out is off for now (Adam, 2026-10-03). The code stays; flip this to bring the button and the first-share offer back. */
const POPOUT_ENABLED = false;
/** How long a guest waits before the trivia card appears. */
const TRIVIA_DELAY_MS = 6000;

interface CallScreenProps {
  readonly kind: RoomKind;
  readonly slug: string;
  readonly name: string;
  /** The room owner's first name, when known, for the waiting line. */
  readonly hostName?: string | null;
  /** Take this account's seat over from another device instead of joining
   * as a second person. */
  readonly move?: boolean;
  /** The owner pressed Record before joining. */
  readonly record?: boolean;
  readonly onLeft: () => void;
  /** After the call moved to another device: bring it back here. */
  readonly onMoveHere?: () => void;
}

function CenteredMessage({ children, action }: { children: React.ReactNode; action?: JSX.Element }): JSX.Element {
  return (
    <div
      style={{
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "var(--space-4)",
        padding: "var(--space-5)",
        textAlign: "center",
        color: "var(--ink)",
      }}
    >
      <p style={{ fontSize: 17, maxWidth: 320, margin: 0 }}>{children}</p>
      {action}
    </div>
  );
}

function LeaveLink({ onLeave }: { onLeave: () => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onLeave}
      style={{
        minHeight: "var(--tap-min)",
        padding: "0 var(--space-4)",
        borderRadius: "var(--radius-md)",
        border: "1px solid var(--divider)",
        background: "var(--paper)",
        color: "var(--ink)",
        cursor: "pointer",
      }}
    >
      Leave
    </button>
  );
}

export function CallScreen({ kind, slug, name, hostName, move, record, onLeft, onMoveHere }: CallScreenProps): JSX.Element {
  const store = useCallStore(kind, slug, name, { move, record });
  const revealValue = useMemo(() => ({ revealed: store.revealed, ready: store.noteMediaReady }), [store.revealed, store.noteMediaReady]);
  // What the Breeze panel says about recording: on, armed (Record was on at
  // join and it starts once someone else is in), or off.
  const recordingShown: RecordingShown = store.recording ? true : store.recordArmed ? "armed" : false;
  const isHost = store.room?.participants.find((p) => p.id === store.selfId)?.kind === "host";
  // The host's recording-off notice: once someone else is in, until Record is
  // on or he closes it. Closed, it returns only after recording has been on.
  const [recOffClosed, setRecOffClosed] = useState(false);
  useEffect(() => {
    if (store.recording) setRecOffClosed(false);
  }, [store.recording]);
  const someoneElse = store.remoteTiles.length > 0;
  const recOffShown = isHost && someoneElse && !store.recording && !store.recordArmed && !recOffClosed;
  const triviaRoom = showsTrivia(kind, slug);
  // The trivia card holds back for the first seconds of the wait, so a guest
  // who is let in right away is not pulled out of a question they just began
  // (row 476; Adam, 2026-10-30: "show the card only after about 6 s").
  const waitingNow = store.state.phase === "waitingForApproval";
  const [triviaDue, setTriviaDue] = useState(false);
  useEffect(() => {
    if (!waitingNow) {
      setTriviaDue(false);
      return;
    }
    const t = setTimeout(() => setTriviaDue(true), TRIVIA_DELAY_MS);
    return () => clearTimeout(t);
  }, [waitingNow]);
  const roomClosed = store.state.phase === "ended" && store.state.reason === "roomClosed";
  const [dialing, setDialing] = useState(false);
  const [breezeOpen, setBreezeOpen] = useState(false);
  const dockPanel = useDesktopControls();
  const popout = usePopout();
  const [popoutOffer, setPopoutOffer] = useState(false);
  // The operator on a desktop opens with the agenda showing and the Live
  // panel collapsed (Adam, 2026-10-19: "have this collapsed by default when i
  // enter"); the Breeze rail beside the agenda opens it, and the agenda
  // closes from "...".
  const [wsBreezeShut, setWsBreezeShut] = useState(true);
  const [wsPrepShut, setWsPrepShut] = useState(false);
  // Calling a phone into the room is for its owner: the host of a personal
  // room, or whoever made a one-off room (everyone there is a guest, so the
  // Worker checks that one).
  const mayCallPhone = !!getUserKey() && (isHost || kind === "once");
  // The operator hosting gets the prep workspace in the dock in place of the
  // plain Breeze panel; on a phone it opens as a sheet from the call menu.
  const operator = isHost && !!getUserKey() && getKeyHandle()?.toLowerCase() === ROOT_HANDLE;
  const [workspaceSheet, setWorkspaceSheet] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  // Turning blur on goes through a preview first; turning it off does not.
  const [blurPreviewOpen, setBlurPreviewOpen] = useState(false);
  // The one other person's picture shape, and the stage's, for the side by
  // side layout below.
  const [remoteAspect, setRemoteAspect] = useState<{ id: string; ratio: number } | null>(null);
  const [stageEl, setStageEl] = useState<HTMLDivElement | null>(null);
  const [stageSize, setStageSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    if (!stageEl) return;
    const ro = new ResizeObserver(([entry]) => {
      if (entry) setStageSize({ w: entry.contentRect.width, h: entry.contentRect.height });
    });
    ro.observe(stageEl);
    return () => ro.disconnect();
  }, [stageEl]);
  const chooseBlur = store.controls.canBlur
    ? (): void => {
        if (store.controls.blurOn) store.toggleBlur();
        else setBlurPreviewOpen(true);
      }
    : undefined;
  const blurPreview = blurPreviewOpen ? (
    <BlurPreviewSheet
      stream={store.selfStream}
      cameraOn={store.controls.cameraOn}
      mirrored={store.facing === "user"}
      onApply={() => {
        setBlurPreviewOpen(false);
        store.toggleBlur();
      }}
      onCancel={() => setBlurPreviewOpen(false)}
    />
  ) : null;
  const guestNames = (store.room?.participants ?? [])
    .filter((p) => p.kind !== "host")
    .map((p) => p.name)
    .join(",");
  const prep = usePrepNow(operator, guestNames);
  const prepSlug = prep.data?.slug ?? null;
  // The way into this room with video, for a person who is on the phone.
  const videoLink = mayCallPhone
    ? kind === "once"
      ? `${roomOrigin(window.location.origin)}/j/${slug}`
      : roomLink(roomOrigin(window.location.origin), slug)
    : undefined;
  const hangUpPhone = (callId: string): void => {
    api(`/api/cadence/calls/${callId}/cancel`, { method: "POST" }).catch((err) =>
      store.showNotice(err instanceof ApiError ? err.message : "Could not hang up. Try again."),
    );
  };

  // Ended for everyone (close code 4005, an Auto Breeze call hung up): a
  // window another app opened for the call closes itself, so that app's
  // Return to call goes away, and tells its opener first.
  useEffect(() => {
    if (!roomClosed) return;
    try {
      window.opener?.postMessage({ type: "breeze:ended", slug }, "*");
      if (window.parent !== window) window.parent.postMessage({ type: "breeze:ended", slug }, "*");
    } catch {
      // A cross-origin opener that went away.
    }
    if (!window.opener) return;
    const t = setTimeout(() => window.close(), 1200);
    return () => clearTimeout(t);
  }, [roomClosed, slug]);

  // A woosh when he comes into the call and when he leaves it (row 460).
  const enteredRef = useRef(false);
  useEffect(() => {
    if (store.joinedAt === null || enteredRef.current) return;
    enteredRef.current = true;
    playWhoosh("enter");
  }, [store.joinedAt]);

  // A ding when someone new knocks at the waiting room (row 480).
  const dingedRef = useRef(new Set<string>());
  useEffect(() => {
    if (!isHost) return;
    let fresh = false;
    for (const k of store.pendingKnocks) {
      if (!dingedRef.current.has(k.knockId)) {
        dingedRef.current.add(k.knockId);
        fresh = true;
      }
    }
    if (fresh) playDing();
  }, [isHost, store.pendingKnocks]);

  const handleLeave = (): void => {
    if (enteredRef.current) playWhoosh("exit");
    popout.close();
    store.leave();
    onLeft();
  };

  // What the person picked in the share picker decides what may sit in the
  // pop-out; null while they are not sharing.
  const surface = useMemo(() => shareSurface(store.screenStream), [store.screenStream]);
  const phaseNow = store.state.phase;
  const inCall = phaseNow === "connected" || phaseNow === "reconnecting";

  // Leaving the call, or the call ending, takes the pop-out down with it.
  const closePopout = popout.close;
  useEffect(() => {
    if (!inCall) closePopout();
  }, [inCall, closePopout]);

  // When a share starts on a computer, offer the pop-out once. A browser
  // without it is told so once instead, and gets no button.
  const shareStarted = store.screenStream !== null;
  const { supported: popoutSupportedHere, root: popoutRoot } = popout;
  const showNotice = store.showNotice;
  useEffect(() => {
    if (!POPOUT_ENABLED) return;
    if (!shareStarted) {
      setPopoutOffer(false);
      return;
    }
    if (!dockPanel || popoutRoot) return;
    const flag = popoutSupportedHere ? "breeze.popout.offered.v1" : "breeze.popout.unsupported.v1";
    let seen = false;
    try {
      seen = window.localStorage.getItem(flag) === "1";
      if (!seen) window.localStorage.setItem(flag, "1");
    } catch {
      // Storage can be blocked; the offer then shows on each share.
    }
    if (seen) return;
    if (popoutSupportedHere) setPopoutOffer(true);
    else showNotice("Pop out needs Chrome on a computer.");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareStarted]);
  useEffect(() => {
    if (popoutRoot) setPopoutOffer(false);
  }, [popoutRoot]);

  // Hooks stay above the early returns below: a hook after them runs on some
  // renders and not others, and React ends the page (a blank screen on join).
  // Chat: a count of what he has not read, and a toast for a message that
  // arrives while the chat is shut. What was already there on joining is
  // history, not news.
  const selfId = store.selfId ?? null;
  const chatMessages = store.chat.messages;
  const chatUnread = chatMessages.filter((m) => m.authorId !== selfId && m.at > store.chat.readAt).length;
  const chatToasted = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const newest = chatMessages.at(-1);
    if (chatToasted.current === undefined) {
      chatToasted.current = newest?.id ?? null;
      return;
    }
    if (!newest || newest.id === chatToasted.current) return;
    chatToasted.current = newest.id;
    if (chatOpen || newest.authorId === selfId) return;
    const shown = newest.text.replace(/\s+/g, " ");
    showNotice(`${newest.authorName || "Guest"}: ${shown.length > 60 ? `${shown.slice(0, 57)}...` : shown}`);
  }, [chatMessages, chatOpen, selfId, showNotice]);

  // Connecting, and waiting to be let in, keep your own video and the call
  // buttons on screen (Adam, 2026-09-28: "the waiting for adam screen should
  // still show video and buttons").
  const phase = store.state.phase;
  if (phase === "idle" || phase === "previewing" || phase === "connecting" || phase === "waitingForApproval") {
    const line =
      phase === "waitingForApproval" ? waitingText(hostName) : "Connecting…";
    return (
      <div style={{ position: "relative", width: "100%", height: "100dvh", background: "#0b0e12", overflow: "hidden" }}>
        {store.selfStream && store.controls.cameraOn ? (
          <>
            <VideoView stream={store.selfStream} mirrored={store.facing === "user"} muted fit="cover" onReport={store.reportSelfView} />
            <BlurLoading show={store.controls.blurLoading} />
          </>
        ) : (
          <div style={{ display: "grid", placeItems: "center", height: "100%", color: "#c7ccd2" }}>
            <div
              style={{
                display: "grid",
                placeItems: "center",
                width: 96,
                height: 96,
                borderRadius: "50%",
                background: "#2a323b",
                fontSize: 36,
                fontWeight: 600,
                color: "#e7eaee",
              }}
            >
              {(name.trim()[0] ?? "?").toUpperCase()}
            </div>
          </div>
        )}
        <WaitingLine text={line} />
        {store.notice ? (
          <div
            role="status"
            style={{
              position: "absolute",
              top: "calc(env(safe-area-inset-top, 0px) + 76px)",
              left: "50%",
              transform: "translateX(-50%)",
              maxWidth: "calc(100% - 2 * var(--space-4))",
              padding: "10px 14px",
              borderRadius: 12,
              background: "rgba(28,31,36,0.92)",
              color: "#fff",
              fontSize: 14,
              zIndex: 30,
            }}
          >
            {store.notice}
          </div>
        ) : null}
        {phase === "waitingForApproval" && triviaRoom && triviaDue ? <WaitingTrivia onEvent={store.reportTrivia} /> : null}
        {/* Nothing to share or record before the room lets you in. */}
        <CallMenu
          canFlipCamera={store.controls.canFlipCamera}
          onFlipCamera={store.flipCamera}
          canShareScreen={false}
          sharingScreen={false}
          originalSoundOn={false}
          blurOn={store.controls.blurOn}
          onToggleBlur={chooseBlur}
        />
        <ControlsTray
          micOn={store.controls.micOn}
          cameraOn={store.controls.cameraOn}
          onToggleMic={store.toggleMic}
          onToggleCamera={store.toggleCamera}
          onLeave={handleLeave}
        />
        {blurPreview}
      </div>
    );
  }

  if (store.state.phase === "error") {
    return <CenteredMessage action={<LeaveLink onLeave={handleLeave} />}>{store.state.message}</CenteredMessage>;
  }

  if (store.state.phase === "ended" && store.state.reason === "movedAway") {
    return (
      <CenteredMessage
        action={
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "var(--space-2)" }}>
            {onMoveHere ? (
              <button type="button" className="btn btn--primary" onClick={onMoveHere} style={{ height: "var(--tap-min)", minWidth: 160 }}>
                Move here
              </button>
            ) : null}
            <button type="button" className="textlink" onClick={onLeft} style={{ background: "none", border: "none", cursor: "pointer" }}>
              Back
            </button>
          </div>
        }
      >
        Moved to your other device.
      </CenteredMessage>
    );
  }

  if (store.state.phase === "ended") {
    const text =
      store.state.reason === "knockDenied"
        ? hostName
          ? `${hostName} did not let you in.`
          : "You were not let in."
        : store.state.reason === "roomClosed"
          ? "The call ended."
          : "You left the call.";
    return (
      <CenteredMessage
        action={
          <button
            type="button"
            onClick={onLeft}
            style={{
              minHeight: "var(--tap-min)",
              padding: "0 var(--space-4)",
              borderRadius: "var(--radius-md)",
              border: "none",
              background: "var(--accent)",
              color: "var(--accent-contrast)",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Back
          </button>
        }
      >
        {text}
      </CenteredMessage>
    );
  }

  // connected or reconnecting: the actual call surface.
  const sharedByRemote = store.remoteTiles.find((t) => t.screenTrack);
  const isSharingLocally = store.controls.sharingScreen;
  const screenActive = !!sharedByRemote || isSharingLocally;
  const faceTiles = store.remoteTiles;

  // The prep is private notes: while the operator shares a browser that does
  // not say what it is sharing, the dock falls back to the plain panel, whose
  // private tabs collapse on their own. A window, a tab or an entire screen
  // keeps everything: he shares his other monitor (QUEUE rows 443 and 492).
  const shareHidesPrivate = isSharingLocally && !surfaceKeepsPrivate(surface);
  const showWorkspace = operator && !shareHidesPrivate;
  const operatorDock = operator && dockPanel;
  // A guest has nothing to open while the host has Breeze off and shared
  // notes off: no B in the bar, and no panel.
  const guestNoBreeze = !isHost && store.assistant.state !== null && !store.assistant.state.on && store.assistant.state.guestNotes !== true;
  const breezeIsOpen = !guestNoBreeze && (operatorDock ? !wsBreezeShut : breezeOpen);
  const toggleBreeze = (): void => (operatorDock ? setWsBreezeShut((v) => !v) : setBreezeOpen((v) => !v));
  const closeBreeze = (): void => (operatorDock ? setWsBreezeShut(true) : setBreezeOpen(false));
  // One other person on a phone held upright fills only a narrow column of a
  // wide screen, so your own picture takes the other half, as big as theirs,
  // in place of the small tile (Adam, 2026-10-04: "when he joins from a phone
  // like this, i want to see my picture big next to his").
  const only = faceTiles.length === 1 ? faceTiles[0] : undefined;
  const onlyShowsVideo = !!only?.videoTrack && !only.participant.cameraOff;
  const sideBySide =
    !screenActive &&
    !!only &&
    onlyShowsVideo &&
    remoteAspect?.id === only.participant.id &&
    remoteAspect.ratio < 0.9 &&
    stageSize.w >= 600 &&
    stageSize.w > stageSize.h * 1.15 &&
    store.controls.cameraOn &&
    !!store.selfStream;
  const trackAspect = (id: string) => (ratio: number) =>
    setRemoteAspect((was) => (was?.id === id && Math.abs(was.ratio - ratio) < 0.01 ? was : { id, ratio }));
  return (
    <RevealContext.Provider value={revealValue}>
    <div style={{ position: "relative", display: "flex", width: "100%", height: "100dvh", background: "#0b0e12", overflow: "hidden" }}>
    <div ref={setStageEl} style={{ position: "relative", flex: 1, minWidth: 0, height: "100%", overflow: "hidden" }}>
      {screenActive ? (
        <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <div style={{ flex: 1, minHeight: 0 }}>
            {sharedByRemote?.screenTrack ? (
              <VideoView track={sharedByRemote.screenTrack} fit="contain" muted />
            ) : (
              <VideoView stream={store.screenStream} fit="contain" muted />
            )}
          </div>
          {faceTiles.length > 0 && !popout.root ? (
            <div data-testid="share-strip" style={{ display: "flex", gap: "var(--space-2)", padding: "var(--space-2)", overflowX: "auto" }}>
              {faceTiles.map((tile) => (
                <div key={tile.participant.id} style={{ width: 120, height: 90, flexShrink: 0, borderRadius: "var(--radius-sm)", overflow: "hidden" }}>
                  <RemoteTile tile={tile} compact onHangUpPhone={mayCallPhone ? hangUpPhone : undefined} videoLink={videoLink} onLowerHand={isHost ? store.lowerHand : undefined} />
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : faceTiles.length === 0 ? (
        <CenteredMessage>
          <span style={{ color: "#c7ccd2" }}>Waiting for others to join.</span>
        </CenteredMessage>
      ) : only && sideBySide ? (
        <div data-testid="side-by-side" style={{ display: "flex", gap: 2, width: "100%", height: "100%" }}>
          <div style={{ position: "relative", flex: 1, minWidth: 0, height: "100%", background: "#12161b", overflow: "hidden" }}>
            <VideoView stream={store.selfStream} mirrored={store.facing === "user"} muted fit="cover" onReport={store.reportSelfView} />
            <BlurLoading show={store.controls.blurLoading && store.controls.cameraOn} />
            <div
              style={{
                position: "absolute",
                left: "var(--space-3)",
                top: "calc(env(safe-area-inset-top, 0px) + var(--space-3))",
                padding: "6px 12px",
                borderRadius: "var(--radius-sm)",
                background: "rgba(10,13,17,0.55)",
                color: "#fff",
                fontSize: 14,
                display: "flex",
                alignItems: "center",
                gap: "var(--space-2)",
              }}
            >
              <span>You</span>
              {store.controls.handRaised ? <HandChip name="You" /> : null}
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 0, height: "100%" }}>
            <RemoteTile
              key={only.participant.id}
              tile={only}
              onHangUpPhone={mayCallPhone ? hangUpPhone : undefined} videoLink={videoLink}
              onLowerHand={isHost ? store.lowerHand : undefined}
              onAspect={trackAspect(only.participant.id)}
            />
          </div>
        </div>
      ) : only ? (
        <RemoteTile
          key={only.participant.id}
          tile={only}
          onHangUpPhone={mayCallPhone ? hangUpPhone : undefined} videoLink={videoLink}
          onLowerHand={isHost ? store.lowerHand : undefined}
          onAspect={trackAspect(only.participant.id)}
        />
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gridAutoRows: "1fr",
            gap: 2,
            width: "100%",
            height: "100%",
          }}
        >
          {faceTiles.map((tile) => (
            <RemoteTile key={tile.participant.id} tile={tile} onHangUpPhone={mayCallPhone ? hangUpPhone : undefined} videoLink={videoLink} onLowerHand={isHost ? store.lowerHand : undefined} />
          ))}
        </div>
      )}

      {sideBySide ? null : (
        <DraggableSelfView
          stream={store.selfStream}
          cameraOn={store.controls.cameraOn}
          mirrored={store.facing === "user"}
          flipPhase={store.flipPhase}
          handRaised={store.controls.handRaised}
          blurLoading={store.controls.blurLoading}
          onReport={store.reportSelfView}
        />
      )}

      {phaseNow === "reconnecting" ? (
        <div
          role="status"
          style={{
            position: "absolute",
            top: "calc(env(safe-area-inset-top, 0px) + var(--space-4))",
            left: "50%",
            transform: "translateX(-50%)",
            padding: "10px 16px",
            borderRadius: 14,
            background: "rgba(16,21,27,0.82)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            color: "#fff",
            fontSize: 15,
            fontWeight: 550,
            zIndex: 45,
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <span className="callspin" aria-hidden="true" />
          Reconnecting…
        </div>
      ) : null}
      {isHost ? <KnockBanner knocks={store.pendingKnocks} onRespond={store.respondToKnock} /> : null}
      {isHost ? <LateGuestBanner late={store.assistant.lateGuest} onSend={store.assistant.sendNudge} arrived={store.pendingKnocks.length > 0 || someoneElse} /> : null}

      {/* Everyone in the call sees that it is being recorded, not only the
          host who started it. */}
      <RecordingMark recording={store.recording} />
      {/* The running time is the host's alone (Adam, 2026-10-18: "don't let
          guests see time elapsed"). */}
      {isHost && store.joinedAt !== null ? (
        <div
          style={{
            position: "absolute",
            top: "calc(env(safe-area-inset-top, 0px) + var(--space-3))",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 25,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          {store.joinedAt !== null ? <CallClock since={store.joinedAt} /> : null}
        </div>
      ) : null}

      {store.recordingSaved ? (
        <div
          role="status"
          onPointerEnter={() => store.holdRecordingSaved(true)}
          onPointerLeave={() => store.holdRecordingSaved(false)}
          style={{
            position: "absolute",
            bottom: "calc(env(safe-area-inset-bottom, 0px) + 100px)",
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            alignItems: "center",
            gap: 4,
            padding: store.recordingSaved.phase === "saved" && store.recordingSaved.recordingId ? "4px 4px 4px 16px" : "12px 16px",
            borderRadius: 14,
            background: "rgba(28,31,36,0.94)",
            color: "#fff",
            fontSize: 15,
            fontWeight: 550,
            whiteSpace: "nowrap",
            zIndex: 30,
            animation: "breeze-fade 0.16s var(--ease)",
          }}
        >
          <span>
            {store.recordingSaved.phase === "deleted"
              ? "Recording deleted"
              : store.recordingSaved.phase === "deleting"
                ? "Deleting…"
                : "Recording saved"}
          </span>
          {store.recordingSaved.phase === "saved" && store.recordingSaved.recordingId ? (
            <button
              type="button"
              aria-label="Delete recording"
              onClick={store.discardRecording}
              style={{
                display: "grid",
                placeItems: "center",
                width: 40,
                height: 40,
                border: "none",
                borderRadius: 10,
                background: "transparent",
                color: "#ff8a80",
                cursor: "pointer",
              }}
            >
              <TrashIcon size={20} />
            </button>
          ) : null}
        </div>
      ) : null}

      {recOffShown ? (
        <RecordingOffBar
          top={`calc(env(safe-area-inset-top, 0px) + ${store.joinedAt !== null ? "56px" : "var(--space-4)"})`}
          onTurnOn={store.toggleRecording}
          onClose={() => setRecOffClosed(true)}
        />
      ) : null}

      {/* Stays up while the call's sound is broken (useCallStore's media
          check): someone this side cannot hear, someone who cannot hear
          this side, or a microphone sending silence. */}
      {store.mediaAlert ? (
        <div
          role="alert"
          data-testid="media-alert"
          style={{
            position: "absolute",
            top: `calc(env(safe-area-inset-top, 0px) + ${store.recording || store.joinedAt !== null ? "56px" : "var(--space-4)"}${recOffShown ? " + 52px" : ""})`,
            left: "50%",
            transform: "translateX(-50%)",
            width: "max-content",
            maxWidth: "calc(100% - 2 * var(--space-4))",
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: store.mediaAlert.action ? "6px 6px 6px 14px" : "10px 14px",
            borderRadius: 12,
            background: "rgba(28,31,36,0.94)",
            color: "#fff",
            fontSize: 14,
            zIndex: 31,
            animation: "breeze-fade 0.2s var(--ease)",
          }}
        >
          <span>{store.mediaAlert.text}</span>
          {store.mediaAlert.action ? (
            <button
              type="button"
              onClick={store.mediaAlert.action.run}
              style={{
                flexShrink: 0,
                minHeight: 36,
                padding: "0 12px",
                border: "none",
                borderRadius: 8,
                background: "#fff",
                color: "#111",
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {store.mediaAlert.action.label}
            </button>
          ) : null}
        </div>
      ) : null}

      {store.notice ? (
        <div
          role="status"
          style={{
            position: "absolute",
            top: `calc(env(safe-area-inset-top, 0px) + ${store.recording || store.joinedAt !== null ? "56px" : "var(--space-4)"}${recOffShown ? " + 52px" : ""}${store.mediaAlert ? " + 56px" : ""})`,
            left: "50%",
            transform: "translateX(-50%)",
            maxWidth: "calc(100% - 2 * var(--space-4))",
            padding: "10px 14px",
            borderRadius: 12,
            background: "rgba(28,31,36,0.92)",
            color: "#fff",
            fontSize: 14,
            zIndex: 30,
            animation: "breeze-fade 0.2s var(--ease)",
          }}
        >
          {store.notice}
        </div>
      ) : null}

      {/* An Auto Breeze phone call bridged into this room, for its owner. */}
      {kind === "once" ? <CadencePanel slug={slug} /> : null}

      <CallMenu
        canFlipCamera={store.controls.canFlipCamera}
        onFlipCamera={store.flipCamera}
        canShareScreen={store.controls.canShareScreen}
        sharingScreen={store.controls.sharingScreen}
        onToggleScreenShare={store.toggleScreenShare}
        recording={isHost ? store.recording : undefined}
        onToggleRecording={isHost ? store.toggleRecording : undefined}
        originalSoundOn={store.controls.originalSoundOn}
        onToggleOriginalSound={store.toggleOriginalSound}
        blurOn={store.controls.blurOn}
        onToggleBlur={chooseBlur}
        handRaised={store.controls.handRaised}
        onToggleHand={store.toggleHand}
        onCallPhone={mayCallPhone ? () => setDialing(true) : undefined}
        onToggleBreeze={guestNoBreeze || (!BREEZE_PANEL_ENABLED && operatorDock && showWorkspace) ? undefined : toggleBreeze}
        breezeOpen={breezeIsOpen}
        onToggleChat={() => setChatOpen((v) => !v)}
        chatUnread={chatUnread}
        prepOpen={operatorDock ? !wsPrepShut : undefined}
        onOpenPrep={
          operator && !shareHidesPrivate
            ? () => {
                if (dockPanel) setWsPrepShut((v) => !v);
                else {
                  setBreezeOpen(false);
                  setWorkspaceSheet(true);
                }
              }
            : undefined
        }
      />
      <ControlsTray
        micOn={store.controls.micOn}
        cameraOn={store.controls.cameraOn}
        onToggleMic={store.toggleMic}
        onToggleCamera={store.toggleCamera}
        onLeave={handleLeave}
        canShareScreen={store.controls.canShareScreen}
        sharingScreen={store.controls.sharingScreen}
        onToggleScreenShare={store.toggleScreenShare}
        recording={isHost ? store.recording : undefined}
        onToggleRecording={isHost ? store.toggleRecording : undefined}
        onToggleBreeze={guestNoBreeze || (!BREEZE_PANEL_ENABLED && operatorDock && showWorkspace) ? undefined : toggleBreeze}
        breezeOpen={breezeIsOpen}
        onToggleChat={() => setChatOpen((v) => !v)}
        chatOpen={chatOpen}
        chatUnread={chatUnread}
        onTogglePopout={POPOUT_ENABLED && popout.supported ? (popout.root ? popout.close : () => void popout.open()) : undefined}
        popoutOpen={popout.root !== null}
      />
      {popoutOffer ? (
        <PopoutOffer
          surface={surface}
          onOpen={() => {
            setPopoutOffer(false);
            void popout.open();
          }}
          onDismiss={() => setPopoutOffer(false)}
        />
      ) : null}
      {popout.root ? (
        <Popout
          root={popout.root}
          tiles={faceTiles}
          panel={store.panel}
          assistant={store.assistant}
          isHost={isHost}
          surface={isSharingLocally ? surface : null}
        />
      ) : null}
      {blurPreview}
      {dialing ? (
        <PhoneDialer
          kind={kind}
          slug={slug}
          onClose={() => setDialing(false)}
          onPlaced={(who) => store.showNotice(`Calling ${who}`)}
        />
      ) : null}
    </div>
      {dockPanel && showWorkspace ? (
        <div className="cpw">
          <CallPrepWorkspace
            slug={prepSlug}
            panel={store.panel}
            assistant={store.assistant}
            selfId={selfId}
            isHost={isHost}
            sharingScreen={false}
            recording={recordingShown}
            columns
            breezeShut={wsBreezeShut}
            onBreezeShutChange={setWsBreezeShut}
            prepShut={wsPrepShut}
            onPrepShutChange={setWsPrepShut}
          />
        </div>
      ) : null}
      {breezeIsOpen && dockPanel && !showWorkspace ? (
        <BreezePanel panel={store.panel} assistant={store.assistant} selfId={selfId} isHost={isHost} sharingScreen={shareHidesPrivate} shareSurface={isSharingLocally ? surface : null} recording={recordingShown} layout="dock" onClose={closeBreeze} />
      ) : null}
      {workspaceSheet && !dockPanel && showWorkspace ? (
        <WorkspaceSheet title="Agenda" tall onClose={() => setWorkspaceSheet(false)}>
          <CallPrepWorkspace slug={prepSlug} panel={store.panel} assistant={store.assistant} selfId={selfId} isHost={isHost} sharingScreen={false} recording={recordingShown} />
        </WorkspaceSheet>
      ) : null}
      {chatOpen ? (
        <WorkspaceSheet title="Chat" side={dockPanel} tall={!dockPanel} onClose={() => setChatOpen(false)}>
          <ChatPanel chat={store.chat} selfId={selfId} isHost={isHost} />
        </WorkspaceSheet>
      ) : null}
      {breezeIsOpen && !dockPanel ? (
        <BreezeStrip panel={store.panel} assistant={store.assistant} selfId={selfId} isHost={isHost} sharingScreen={isSharingLocally} publicOnly recording={recordingShown} onClose={closeBreeze} />
      ) : null}
    </div>
    </RevealContext.Provider>
  );
}
